/**
 * Episodes API Client with React Query Hooks
 *
 * Provides type-safe API client for Episode Management System using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 *
 * Story 3.9.15: Episodes are now language-specific with voice_ids directly.
 * Localization-related code has been removed.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

// Episode Status Enum
const EpisodeStatusSchema = z.enum(['draft', 'active', 'deactivated']);

// Story 15.11: Duration ID (1=Short, 2=Medium, 3=Long, 4=Extended)
const DurationIdSchema = z.number().int().min(1).max(4);

// Persona Schema
// Story 3.11: Added thumbnailUrl for persona visual assets
const PersonaSchema = z.object({
  name: z.string().min(1).max(100),
  role: z.string().min(1).max(100),
  description: z.string().min(1).max(500),
  thumbnailUrl: z.string().url().nullable().optional(), // Story 3.11: Persona thumbnail
});

// Story 3.11: Localized dictionary content schema
const LocalizedDictionaryContentSchema = z.object({
  explanation: z.string().min(1),
  examplePhrases: z.array(z.string().min(1)).min(1).max(3),
});

// Story 3.11: Dictionary entry schema for contextual vocabulary
const DictionaryEntrySchema = z.object({
  word: z.string().min(1),
  explanation: z.string().min(1),
  examplePhrases: z.array(z.string().min(1)).min(1).max(3),
  localized: z.record(z.string(), LocalizedDictionaryContentSchema).nullable().optional(),
});

// Tag Schema (read-only after Story 15.7.2 - tags are seeded via migration)
// Story 15.8: Tag IDs are now integers (migrated from UUID)
const TagResponseSchema = z.object({
  id: z.number().int(),
  name: z.string().max(100), // English name from list_tags_admin
  learningGoalIds: z.array(z.number().int()),
  createdAt: z.string(),
  updatedAt: z.string(),
  episodeCount: z.number().int().nonnegative(),
});

// Episode Schemas
// Story 3.9.15: Episodes are now language-specific
// - description removed (use context instead)
// - languageCode added (required)
// - voiceIds NOT in create/update (assigned separately via PUT /episodes/{id}/voices)
// Story 15.8: tagIds are now integers (migrated from UUID)
const _EpisodeCreateSchema = z.object({
  id: z.string().uuid().optional(), // Pre-generated UUID for image asset re-upload on import
  name: z.string().max(200),
  languageCode: z.string().min(2).max(10), // Story 3.9.15: Required language code
  durationId: DurationIdSchema, // Story 15.11: Duration ID (1-4)
  context: z.string(),
  goals: z.array(z.string().min(1)).min(1).max(10),
  personas: z.array(PersonaSchema).min(1).max(5),
  scenario: z.string(),
  complexityLevel: z.number().int().min(2).max(5),
  tagIds: z.array(z.number().int()),
  // Story 3.11: Visual assets and dictionary
  thumbnailUrl: z.string().url().nullable().optional(),
  backgroundImageUrl: z.string().url().nullable().optional(),
  dictionary: z.array(DictionaryEntrySchema).max(10).nullable().optional(),
});

// Story 15.8: tagIds are now integers (migrated from UUID)
const _EpisodeUpdateSchema = z.object({
  name: z.string().max(200).optional(),
  languageCode: z.string().min(2).max(10).optional(), // Story 3.9.15
  durationId: DurationIdSchema.optional(), // Story 15.11: Duration ID (1-4)
  context: z.string().optional(),
  goals: z.array(z.string().min(1)).min(1).max(10).optional(),
  personas: z.array(PersonaSchema).min(1).max(5).optional(),
  scenario: z.string().optional(),
  complexityLevel: z.number().int().min(2).max(5).optional(),
  tagIds: z.array(z.number().int()).optional(),
  // Story 3.11: Visual assets and dictionary
  thumbnailUrl: z.string().url().nullable().optional(),
  backgroundImageUrl: z.string().url().nullable().optional(),
  dictionary: z.array(DictionaryEntrySchema).max(10).nullable().optional(),
});

// Story 15.11: Episode duration response object structure
// Backend returns only id and maxTurns; name is resolved locally from initial data
const EpisodeDurationResponseSchema = z.object({
  id: z.number(),
  maxTurns: z.number(),
});

// Story 3.16: Voice group response schema
const VoiceGroupResponseSchema = z.object({
  id: z.string().uuid(),
  localeCode: z.string().nullable(),
  localeName: z.string().nullable(),
  voiceIds: z.array(z.number().int()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Story 3.16: Updated episode response with voice groups
const EpisodeResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  languageCode: z.string(),
  duration: EpisodeDurationResponseSchema,
  context: z.string(),
  goals: z.array(z.string()),
  personas: z.array(PersonaSchema),
  scenario: z.string(),
  complexityLevel: z.number(),
  tagIds: z.array(z.number().int()),
  voiceGroups: z.array(VoiceGroupResponseSchema).default([]),
  status: EpisodeStatusSchema,
  thumbnailUrl: z.string().url().nullable().optional(),
  backgroundImageUrl: z.string().url().nullable().optional(),
  dictionary: z.array(DictionaryEntrySchema).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Lean schema returned by GET /api/admin/episodes (list endpoint).
// Heavy fields (scenario/context/goals/personas/dictionary/voice_groups/background)
// are dropped; use GET /api/admin/episodes/{id} to fetch the full body.
const EpisodeListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  languageCode: z.string(),
  durationId: z.number().int(),
  complexityLevel: z.number(),
  tagIds: z.array(z.number().int()),
  thumbnailUrl: z.string().url().nullable().optional(),
  status: EpisodeStatusSchema,
  personasCount: z.number().int(),
  voiceGroupsCount: z.number().int(),
  updatedAt: z.string(),
});

// Export TypeScript types from Zod schemas
export type EpisodeStatus = z.infer<typeof EpisodeStatusSchema>;
export type EpisodeDurationResponse = z.infer<typeof EpisodeDurationResponseSchema>;
export type Persona = z.infer<typeof PersonaSchema>;
export type TagResponse = z.infer<typeof TagResponseSchema>;
export type EpisodeCreate = z.infer<typeof _EpisodeCreateSchema>;
export type EpisodeUpdate = z.infer<typeof _EpisodeUpdateSchema>;
export type EpisodeResponse = z.infer<typeof EpisodeResponseSchema>;
export type EpisodeListItem = z.infer<typeof EpisodeListItemSchema>;
export type VoiceGroupResponse = z.infer<typeof VoiceGroupResponseSchema>;
export type LocalizedDictionaryContent = z.infer<typeof LocalizedDictionaryContentSchema>;
export type DictionaryEntry = z.infer<typeof DictionaryEntrySchema>;

// Episode filters for list endpoint
export interface EpisodeFilters {
  page?: number;
  limit?: number;
  status?: EpisodeStatus;
  complexity?: number[];
  tags?: string[];
  languageCode?: string; // Story 3.9.15: Filter by language
}

// ============================================================================
// STEP 2: Transform Functions (snake_case <-> camelCase)
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase frontend types to snake_case API request
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

// ============================================================================
// STEP 3: Define Fetcher Functions
// ============================================================================

const fetchEpisodes = async (filters?: EpisodeFilters): Promise<EpisodeListItem[]> => {
  try {
    // Transform and filter out undefined values
    const rawParams = filters
      ? transformCamelToSnake(filters as unknown as Record<string, unknown>)
      : undefined;
    // Remove undefined values to ensure clean query params
    const params = rawParams
      ? Object.fromEntries(Object.entries(rawParams).filter(([, v]) => v !== undefined))
      : undefined;
    logger.debug('Fetching episodes with params:', params);
    const data = await apiGet<unknown[]>('/api/admin/episodes', params);
    return z
      .array(EpisodeListItemSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching episodes:', error);
    throw error;
  }
};

export const fetchEpisode = async (episodeId: string): Promise<EpisodeResponse> => {
  try {
    const data = await apiGet<unknown>(`/api/admin/episodes/${episodeId}`);
    return EpisodeResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error fetching episode:', error);
    throw error;
  }
};

const createEpisode = async (episode: EpisodeCreate): Promise<EpisodeResponse> => {
  try {
    const payload = transformCamelToSnake(episode as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>('/api/admin/episodes', payload);
    return EpisodeResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error creating episode:', error);
    throw error;
  }
};

const updateEpisode = async (
  episodeId: string,
  update: EpisodeUpdate
): Promise<EpisodeResponse> => {
  try {
    const payload = transformCamelToSnake(update as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(`/api/admin/episodes/${episodeId}`, payload);
    return EpisodeResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating episode:', error);
    throw error;
  }
};

const deleteEpisode = async (episodeId: string): Promise<void> => {
  try {
    await apiDelete<void>(`/api/admin/episodes/${episodeId}`);
  } catch (error) {
    logger.error('Error deleting episode:', error);
    throw error;
  }
};

const activateEpisode = async (episodeId: string): Promise<EpisodeResponse> => {
  try {
    const data = await apiPost<unknown>(`/api/admin/episodes/${episodeId}/activate`, {});
    return EpisodeResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error activating episode:', error);
    throw error;
  }
};

const deactivateEpisode = async (episodeId: string): Promise<EpisodeResponse> => {
  try {
    const data = await apiPost<unknown>(`/api/admin/episodes/${episodeId}/deactivate`, {});
    return EpisodeResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error deactivating episode:', error);
    throw error;
  }
};

const fetchTags = async (): Promise<TagResponse[]> => {
  try {
    // Request max tags (backend limit is 100)
    const data = await apiGet<unknown[]>('/api/admin/tags', { limit: 100 });
    return z
      .array(TagResponseSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching tags:', error);
    throw error;
  }
};

// Story 3.16: Voice Group CRUD functions
const fetchVoiceGroups = async (episodeId: string): Promise<VoiceGroupResponse[]> => {
  const data = await apiGet<unknown[]>(`/api/admin/episodes/${episodeId}/voice-groups`);
  return z
    .array(VoiceGroupResponseSchema)
    .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
};

const createVoiceGroup = async (
  episodeId: string,
  localeCode: string | null,
  voiceIds: number[]
): Promise<VoiceGroupResponse> => {
  const payload = transformCamelToSnake({ localeCode, voiceIds } as unknown as Record<
    string,
    unknown
  >);
  const data = await apiPost<unknown>(`/api/admin/episodes/${episodeId}/voice-groups`, payload);
  return VoiceGroupResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
};

const updateVoiceGroup = async (
  episodeId: string,
  groupId: string,
  voiceIds: number[]
): Promise<VoiceGroupResponse> => {
  const payload = transformCamelToSnake({ voiceIds } as unknown as Record<string, unknown>);
  const data = await apiPut<unknown>(
    `/api/admin/episodes/${episodeId}/voice-groups/${groupId}`,
    payload
  );
  return VoiceGroupResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
};

const deleteVoiceGroup = async (episodeId: string, groupId: string): Promise<void> => {
  await apiDelete<void>(`/api/admin/episodes/${episodeId}/voice-groups/${groupId}`);
};

// ============================================================================
// STEP 4: Define React Query Hooks
// ============================================================================

/**
 * Fetch all episodes with optional filters
 */
export const useEpisodes = (filters?: EpisodeFilters) => {
  return useQuery({
    queryKey: ['episodes', filters],
    queryFn: () => fetchEpisodes(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetch single episode by ID
 */
export const useEpisode = (episodeId: string | undefined) => {
  return useQuery({
    queryKey: ['episode', episodeId],
    queryFn: () => fetchEpisode(episodeId!),
    enabled: !!episodeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Create new episode
 */
export const useCreateEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEpisode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};

/**
 * Update existing episode
 */
export const useUpdateEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ episodeId, update }: { episodeId: string; update: EpisodeUpdate }) =>
      updateEpisode(episodeId, update),
    onSuccess: (updatedEpisode) => {
      // Optimistic update - update the specific episode in cache
      queryClient.setQueryData(['episode', updatedEpisode.id], updatedEpisode);

      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};

/**
 * Delete episode (hard delete drafts, soft delete active)
 */
export const useDeleteEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEpisode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};

/**
 * Activate episode (sets status to active)
 * Story 3.9.15: Requires voiceIds to be assigned first
 */
export const useActivateEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: activateEpisode,
    onSuccess: (updatedEpisode) => {
      queryClient.setQueryData(['episode', updatedEpisode.id], updatedEpisode);
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};

/**
 * Deactivate episode (sets status to deactivated)
 */
export const useDeactivateEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivateEpisode,
    onSuccess: (updatedEpisode) => {
      queryClient.setQueryData(['episode', updatedEpisode.id], updatedEpisode);
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};

/**
 * Fetch all tags
 */
export const useTags = () => {
  return useQuery({
    queryKey: ['tags'],
    queryFn: fetchTags,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Story 3.16: Voice Group hooks

export const useVoiceGroups = (episodeId: string | undefined) => {
  return useQuery({
    queryKey: ['voiceGroups', episodeId],
    queryFn: () => fetchVoiceGroups(episodeId!),
    enabled: !!episodeId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateVoiceGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      episodeId,
      localeCode,
      voiceIds,
    }: {
      episodeId: string;
      localeCode: string | null;
      voiceIds: number[];
    }) => createVoiceGroup(episodeId, localeCode, voiceIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['voiceGroups', variables.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['episode', variables.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};

export const useUpdateVoiceGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      episodeId,
      groupId,
      voiceIds,
    }: {
      episodeId: string;
      groupId: string;
      voiceIds: number[];
    }) => updateVoiceGroup(episodeId, groupId, voiceIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['voiceGroups', variables.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['episode', variables.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};

export const useDeleteVoiceGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ episodeId, groupId }: { episodeId: string; groupId: string }) =>
      deleteVoiceGroup(episodeId, groupId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['voiceGroups', variables.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['episode', variables.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
    },
  });
};
